import React, { useEffect, useMemo, useState } from "react";

// Shared dashboard design system — the same visual language used across the
// member-facing dashboards (see CoachDashboard). Centralised here so the
// back-office (admin) pages reuse one source of truth instead of duplicating
// colours/classes. Tokens and primitives match the member dashboards exactly.

// ── Design tokens (member palette) ──────────────────────────────────────────
export const TOKENS = {
  PRIMARY: "#1d4ed8", // blue-700 — accents, primary actions
  PRIMARY_DARK: "#1e3a8a", // blue-900 — icon-chip gradients
  SIDEBAR_TOP: "#172554", // blue-950 — sidebar gradient top
  SIDEBAR_BOTTOM: "#1e3a8a", // blue-900 — sidebar gradient bottom
  NAVY: "#0f1c3f", // headings
  LIGHT: "#e8f0fe", // icon chip / soft accent background
};

// Page gradient + font used by the in-app (logged-in) screens.
export const PAGE_BG = "bg-gradient-to-br from-blue-50 via-white to-blue-100";
export const APP_FONT = "Inter, system-ui, sans-serif";

// Shared form-control classes (large, rounded, blue focus ring).
export const inputClass =
  "w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition";

export const labelClass = "block text-sm font-medium text-gray-700 mb-1.5";

// Primary / secondary button class strings (consistent with the member pages).
export const btnPrimary =
  "inline-flex items-center justify-center gap-2 bg-blue-700 hover:bg-blue-800 active:bg-blue-900 disabled:bg-blue-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg text-sm px-4 py-2.5 transition shadow-sm hover:shadow-md";

export const btnSecondary =
  "inline-flex items-center justify-center gap-2 border border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold rounded-lg text-sm px-4 py-2.5 transition";

// ── Icon (multi-path supported via "|" separator) ───────────────────────────
export const Icon = ({ path, className = "w-5 h-5", stroke = "currentColor", width = 1.8 }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke={stroke}>
    {path.split("|").map((d, i) => (
      <path key={i} strokeLinecap="round" strokeLinejoin="round" strokeWidth={width} d={d} />
    ))}
  </svg>
);

export const ICONS = {
  dashboard:
    "M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z",
  users:
    "M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4z",
  userCheck: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM6 21v-2a4 4 0 014-4h1m6 3l2 2 4-4",
  roles:
    "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z",
  key: "M15 7a2 2 0 012 2m4-2a6 6 0 01-7.743 5.743L11 14H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z",
  logout:
    "M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1",
  menu: "M4 6h16M4 12h16M4 18h16",
  chevron: "M19 9l-7 7-7-7",
  plus: "M12 4v16m8-8H4",
  search: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z",
  edit: "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z",
  user: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z",
  settings:
    "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z|M15 12a3 3 0 11-6 0 3 3 0 016 0z",
  x: "M6 18L18 6M6 6l12 12",
  check: "M5 13l4 4L19 7",
  shield: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
  arrowRight: "M9 5l7 7-7 7",
  eye: "M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z|M15 12a3 3 0 11-6 0 3 3 0 016 0z",
  printer: "M6 9V4a1 1 0 011-1h10a1 1 0 011 1v5|M6 18H4a1 1 0 01-1-1v-5a1 1 0 011-1h16a1 1 0 011 1v5a1 1 0 01-1 1h-2|M6 14h12v7H6v-7z",
  card: "M3 7a1 1 0 011-1h16a1 1 0 011 1v10a1 1 0 01-1 1H4a1 1 0 01-1-1V7z|M3 10h18|M7 15h4",
  facility: "M3 21h18|M5 21V7l7-4 7 4v14|M9 9h1|M9 13h1|M14 9h1|M14 13h1|M10 21v-4h4v4",
  clubs: "M5 3v18|M5 4h13l-3 4 3 4H5",
  bookings: "M8 2v4|M16 2v4|M3 10h18|M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z",
  payments: "M3 6a2 2 0 012-2h14a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2V6z|M3 10h18|M7 15h2",
  fees: "M12 2v20|M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6",
  attendance: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2|M9 5a2 2 0 012-2h2a2 2 0 012 2v0H9v0z|M9 13l2 2 4-4",
  block: "M12 2a10 10 0 100 20 10 10 0 000-20z|M8 8l8 8",
  download: "M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5|M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3",
  clock: "M12 8v4l3 3|M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  filter: "M3 4h18l-7 8v6l-4 2v-8L3 4z",
  refresh: "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15",
  alertCircle: "M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  bell: "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9",
  phone:
    "M3 5a2 2 0 012-2h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z",
  home: "M3 12l2-2m0 0l7-7 7 7m-9-2v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6",
  tag: "M7 7h.01M3 11V6a2 2 0 012-2h5a2 2 0 011.414.586l9 9a2 2 0 010 2.828l-6 6a2 2 0 01-2.828 0l-9-9A2 2 0 013 11z",
  sun: "M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z",
  bulb: "M9.663 17h4.673M12 3a6 6 0 00-3.528 10.851c.63.632.986 1.489.986 2.383V16h5.084v-.766c0-.894.356-1.751.986-2.383A6 6 0 0012 3z",
  minus: "M5 12h14",
  // Lightning-bolt motif — distinct from `bulb`, used where "lighting turns
  // on automatically at this time" needs its own icon (e.g. a facility's
  // light_required_from row).
  lighting: "M13 2L3 14h7v8l10-12h-7z",
};

// ── Status pill (same colour logic as the member dashboards) ────────────────
export const statusColor = (status) => {
  const s = String(status || "").toLowerCase();
  if (s.includes("active") || s.includes("paid") || s.includes("issued") || s.includes("approved") || s.includes("complete"))
    return { bg: "#dcfce7", color: "#15803d" };
  if (s.includes("pending") || s.includes("await") || s.includes("inactive")) return { bg: "#fef3c7", color: "#b45309" };
  if (s.includes("reject") || s.includes("fail") || s.includes("refund") || s.includes("deactiv")) return { bg: "#fee2e2", color: "#b91c1c" };
  return { bg: "#e5e7eb", color: "#4b5563" };
};

export const Pill = ({ children }) => {
  const c = statusColor(children);
  return (
    <span
      className="text-xs font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap"
      style={{ backgroundColor: c.bg, color: c.color }}
    >
      {children || "—"}
    </span>
  );
};

// ── Avatar (photo or initials) ──────────────────────────────────────────────
export const Avatar = ({ name, url, size = 40 }) => {
  const initials = String(name || "?")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return url ? (
    <img src={url} alt={name} className="rounded-full object-cover flex-shrink-0" style={{ width: size, height: size }} />
  ) : (
    <div
      className="rounded-full flex items-center justify-center flex-shrink-0 font-bold text-white"
      style={{ width: size, height: size, backgroundColor: TOKENS.PRIMARY, fontSize: size * 0.36 }}
    >
      {initials}
    </div>
  );
};

// ── Stat card ───────────────────────────────────────────────────────────────
// `iconSrc` (a static illustration with its own colored badge background
// baked in, e.g. public/images/dashboard/icons/*) takes priority over the
// `icon`/`accent` SVG-path pair — the two are mutually exclusive, since an
// iconSrc image already supplies its own background chip.
export const StatCard = ({ label, value, icon, iconSrc, accent = TOKENS.PRIMARY }) => (
  <div className="bg-white rounded-2xl p-5 shadow-sm flex items-center gap-4">
    {iconSrc ? (
      <img src={iconSrc} alt="" className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />
    ) : (
      <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: TOKENS.LIGHT }}>
        <Icon path={icon} className="w-6 h-6" stroke={accent} width={2} />
      </div>
    )}
    <div className="min-w-0">
      <p className="font-extrabold truncate text-2xl" style={{ color: TOKENS.NAVY }}>
        {value == null ? "—" : value}
      </p>
      <p className="text-xs text-gray-400">{label}</p>
    </div>
  </div>
);

// ── Empty state ─────────────────────────────────────────────────────────────
export const EmptyState = ({ icon, text }) => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    <div className="w-14 h-14 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: TOKENS.LIGHT }}>
      <Icon path={icon} className="w-7 h-7" stroke={TOKENS.PRIMARY} width={1.6} />
    </div>
    <p className="text-sm text-gray-400">{text}</p>
  </div>
);

// ── Pagination ──────────────────────────────────────────────────────────────
// Default page size for every admin list/table — kept in one place so "10-15
// rows per page" reads the same across pages instead of each one picking its
// own number.
export const DEFAULT_PAGE_SIZE = 15;

// Client-side pagination for a page whose list-fetching already returns the
// complete array (no server-side page/per_page support on that endpoint).
// `resetKey` should be a value (or joined string) that changes whenever
// search/filter/tab state changes upstream — the hook resets to page 1 when
// it does, same convention BookingLogs.jsx already uses for its own
// server-paginated search. Also self-clamps if the item count shrinks below
// the current page (e.g. the last page's only row gets filtered out).
export const usePagination = (items, { pageSize = DEFAULT_PAGE_SIZE, resetKey } = {}) => {
  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage(1);
    // Only the reset signal should trigger this — not every items/pageSize
    // change (that's handled by the clamp below instead).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);
  const totalPages = Math.max(1, Math.ceil((items?.length || 0) / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageItems = useMemo(
    () => (items || []).slice((safePage - 1) * pageSize, safePage * pageSize),
    [items, safePage, pageSize]
  );
  return { page: safePage, setPage, totalPages, pageItems };
};

// Consistent numbered pagination control for every admin list/table page —
// Previous | 1 | 2 | … | N | Next, current page highlighted, Previous/Next
// disabled at the ends. Renders nothing when there's only one page (or zero),
// so pages with few records look exactly as they did before pagination
// existed. Works for both server-side pages (pass the backend's page/last
// page, wire onPageChange to re-fetch) and client-side ones (pair with
// usePagination above).
export const Pagination = ({ page, totalPages, onPageChange, className = "" }) => {
  if (!totalPages || totalPages <= 1) return null;

  // Always show first + last page, a small window around the current page,
  // and collapse any gap into a single "…" — keeps the control a fixed
  // width instead of listing out hundreds of pages.
  const windowed = [1];
  for (let p = Math.max(2, page - 1); p <= Math.min(totalPages - 1, page + 1); p++) windowed.push(p);
  if (totalPages > 1) windowed.push(totalPages);
  const items = [];
  let prev = null;
  for (const p of windowed) {
    if (prev != null && p - prev > 1) items.push(`ellipsis-${p}`);
    items.push(p);
    prev = p;
  }

  return (
    <nav className={`flex items-center justify-center flex-wrap gap-1.5 ${className}`} aria-label="Pagination">
      <button
        type="button"
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
        className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition"
      >
        Previous
      </button>
      {items.map((p) =>
        typeof p === "string" ? (
          <span key={p} className="px-1.5 text-sm text-gray-400 select-none">
            …
          </span>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => onPageChange(p)}
            aria-current={p === page ? "page" : undefined}
            className={`min-w-[2.25rem] px-2.5 py-1.5 rounded-lg text-sm font-semibold transition ${
              p === page ? "text-white" : "text-gray-600 border border-gray-200 hover:bg-gray-50"
            }`}
            style={p === page ? { backgroundColor: TOKENS.PRIMARY } : undefined}
          >
            {p}
          </button>
        )
      )}
      <button
        type="button"
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages}
        className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition"
      >
        Next
      </button>
    </nav>
  );
};

// ── Spinner ─────────────────────────────────────────────────────────────────
export const Spinner = ({ className = "w-8 h-8 text-blue-600" }) => (
  <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
  </svg>
);
